import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Icon } from '../../design/Icon';
import { Dialog, useToast } from '../../components/shared/Overlay';
import { EmptyState, ErrorState } from '../../components/shared/States';
import { errorMessage, pstnAPI } from '../../services/reportsApi';
import { plainSyncReason } from './pstnText';
import {
  beforeHistoryDays,
  calendarCells,
  currentMonth,
  classifyDifference,
  pstnBannerTail,
  sortDifferences,
  durationText,
  formatPhone,
  monthLabel,
  monthRangeText,
  previousMonth,
  pstnDirection,
  relativeDay,
  shiftMonth,
  shortDateTime,
} from './reportsLogic';

const DAY_HEADS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const SHOWN_DIFFERENCES = 5;
const integer = (n) => Number(n || 0).toLocaleString('en-US');
const onDay = (value) => {
  const day = relativeDay(value);
  return day === 'Today' || day === 'Yesterday' ? day.toLowerCase() : day;
};
const BASIS_TEXT = { GraphDistinctCallIdCount: 'distinct calls', GraphPstnRowCount: 'billable log rows' };

function MarkDialog({ open, reconciliation, month, onClose, onMarked }) {
  const toast = useToast();
  const [basis, setBasis] = useState('GraphPstnRowCount');
  const [saving, setSaving] = useState(false);
  if (!open || !reconciliation) return null;

  const submit = async () => {
    setSaving(true);
    try {
      const mark = await pstnAPI.markInvoiced(month, basis);
      toast(`Marked ${monthLabel(month)} as invoiced: ${integer(mark.invoicedCallCount)} ${BASIS_TEXT[mark.countBasis]}`);
      onMarked(mark);
    } catch (e) {
      toast(await errorMessage(e, "The month couldn't be marked"), 'alert');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={`Mark ${monthLabel(month)} as invoiced`}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={submit} disabled={saving}>
            Mark as invoiced
          </button>
        </>
      }
    >
      <div className="stack">
        <p className="small">Record the count you invoiced. Re-running the reconciliation later keeps this mark.</p>
        <label className="check">
          <input type="radio" name="basis" checked={basis === 'GraphPstnRowCount'} onChange={() => setBasis('GraphPstnRowCount')} />
          <span>
            Billable count, rows in the Microsoft Graph PSTN log: <strong>{integer(reconciliation.graphPstnRowCount)}</strong>
          </span>
        </label>
        <label className="check">
          <input type="radio" name="basis" checked={basis === 'GraphDistinctCallIdCount'} onChange={() => setBasis('GraphDistinctCallIdCount')} />
          <span>
            Distinct calls in the Microsoft Graph PSTN log: <strong>{integer(reconciliation.graphDistinctCallIdCount)}</strong>
          </span>
        </label>
      </div>
    </Dialog>
  );
}

/** PSTN reconciliation (ManageBilling): month picker, Graph vs stored counts, day calendar, difference list, invoice mark. */
export function PstnTab() {
  const toast = useToast();
  const [month, setMonth] = useState(() => previousMonth());
  const [data, setData] = useState({ status: 'loading' });
  const [busy, setBusy] = useState(null);
  const [markOpen, setMarkOpen] = useState(false);

  const load = useCallback(async (key) => {
    setData({ status: 'loading' });
    try {
      const reconciliation = await pstnAPI.getReconciliation(key);
      const [days, unmatched, mark] = await Promise.all([
        pstnAPI.getDays(key).catch(() => []),
        pstnAPI.getCalls(key, false, 1, 500).catch(() => ({ items: [], pagination: { totalRecords: 0 } })),
        pstnAPI.getInvoiceMark(key).catch(() => null),
      ]);
      setData({ status: 'ready', reconciliation, days, unmatched, mark });
    } catch (error) {
      const status = error.response?.status;
      setData({ status: status === 404 ? 'notRun' : status === 403 ? 'forbidden' : 'error', error });
    }
  }, []);

  useEffect(() => {
    load(month);
  }, [month, load]);

  const rerun = async () => {
    setBusy('run');
    try {
      await pstnAPI.runReconciliation(month);
      toast(`Reconciled ${monthLabel(month)}`);
      await load(month);
    } catch (e) {
      toast(await errorMessage(e, "The reconciliation couldn't run"), 'alert');
    } finally {
      setBusy(null);
    }
  };

  const exportXlsx = async () => {
    setBusy('export');
    const result = await pstnAPI.exportMonth(month);
    setBusy(null);
    toast(result.success ? `Exported ${result.filename}` : result.error, result.success ? 'checkCircle' : 'alert');
  };

  const unmark = async () => {
    setBusy('unmark');
    try {
      await pstnAPI.unmarkInvoiced(month);
      toast(`${monthLabel(month)} is no longer marked as invoiced`);
      setData((d) => ({ ...d, mark: null }));
    } catch (e) {
      toast(await errorMessage(e, "The invoice mark couldn't be removed"), 'alert');
    } finally {
      setBusy(null);
    }
  };

  const { reconciliation, days, unmatched, mark } = data;
  const differences = useMemo(() => {
    const gaps = beforeHistoryDays(days);
    return sortDifferences((unmatched?.items || []).map((row) => ({ row, ...classifyDifference(row, reconciliation, gaps) })));
  }, [unmatched, reconciliation, days]);
  const cells = useMemo(() => calendarCells(days), [days]);
  const ready = data.status === 'ready';

  const header = (
    <div className="row-wrap between">
      <div className="row">
        <button type="button" className="btn icon-btn" aria-label="Previous month" onClick={() => setMonth(shiftMonth(month, -1))}>
          <Icon name="chevL" />
        </button>
        <div className="section-title" style={{ minWidth: 150, textAlign: 'center' }}>
          {monthLabel(month)}
        </div>
        <button type="button" className="btn icon-btn" aria-label="Next month" onClick={() => setMonth(shiftMonth(month, 1))} disabled={month >= currentMonth()}>
          <Icon name="chevR" />
        </button>
      </div>
      <div className="row">
        <button type="button" className="btn" onClick={rerun} disabled={busy === 'run' || data.status === 'forbidden'}>
          <Icon name="refresh" className="ico-sm" />
          {data.status === 'notRun' ? 'Run reconciliation' : 'Re-run reconciliation'}
        </button>
        <button type="button" className="btn" id="b-rec" onClick={exportXlsx} disabled={!ready || busy === 'export'}>
          <Icon name="download" className="ico-sm" />
          Export .xlsx
        </button>
        {mark ? (
          <button type="button" className="btn" onClick={unmark} disabled={busy === 'unmark'} title={`Marked by ${mark.invoicedBy}; click to undo`}>
            <Icon name="check" className="ico-sm" />
            Invoiced {onDay(mark.invoicedAt)} · Undo
          </button>
        ) : (
          <button type="button" className="btn btn-primary" onClick={() => setMarkOpen(true)} disabled={!ready}>
            Mark month as invoiced
          </button>
        )}
      </div>
    </div>
  );

  if (data.status === 'forbidden') {
    return (
      <EmptyState icon="lock" title="PSTN reconciliation needs the Manage billing permission" body="Ask an account administrator if you reconcile or invoice PSTN calls." />
    );
  }

  let body;
  if (data.status === 'notRun') {
    body = (
      <EmptyState
        icon="pstn"
        title={`${monthLabel(month)} hasn't been reconciled yet`}
        body="Run the reconciliation to compare the Microsoft Graph PSTN log with the stored calls for this month."
      />
    );
  } else if (data.status === 'error') {
    body = <ErrorState title="The reconciliation couldn't load" body="The PSTN service didn't respond. Try again in a moment." error={data.error} onRetry={() => load(month)} />;
  } else {
    const r = reconciliation || {};
    const total = unmatched?.pagination?.totalRecords ?? differences.length;
    const willSync = differences.filter((d) => d.status === 'Will sync').length;
    const review = differences.filter((d) => d.status === 'Investigate').length;
    const billable = mark ? mark.invoicedCallCount : r.graphPstnRowCount;
    body = (
      <>
        <section className={['kpis', ready ? '' : 'is-loading'].filter(Boolean).join(' ')}>
          <div className="kpi">
            <div className="k-label">Microsoft Graph PSTN log</div>
            <div className="k-row">
              <div className="k-value">{integer(r.graphDistinctCallIdCount)}</div>
            </div>
            <div className="k-meta">distinct calls, PSTN log, {monthRangeText(month)}</div>
          </div>
          <div className="kpi">
            <div className="k-label">Stored calls with PSTN legs</div>
            <div className="k-row">
              <div className="k-value">{integer(r.storedPstnCallCount)}</div>
            </div>
            <div className="k-meta">Vertolytics call records</div>
          </div>
          <div className="kpi">
            <div className="k-label">Difference</div>
            <div className="k-row">
              <div className="k-value" style={r.difference ? { color: 'var(--warning)' } : undefined}>
                {integer(r.difference)}
              </div>
            </div>
            <div className="k-meta">{r.graphDistinctCallIdCount ? `${((r.difference / r.graphDistinctCallIdCount) * 100).toFixed(2)}% of distinct Graph call IDs` : 'No Graph calls'}</div>
          </div>
          <div className="kpi">
            <div className="k-label">Billable count</div>
            <div className="k-row">
              <div className="k-value">{integer(billable)}</div>
            </div>
            <div className="k-meta">
              {mark && mark.countBasis === 'GraphDistinctCallIdCount' ? 'distinct calls' : `log rows · ${integer(r.graphDistinctCallIdCount)} distinct calls`}
            </div>
            <div className="k-meta">
              {mark ? (
                <span className="badge b-good">
                  <Icon name="check" />
                  Invoiced {onDay(mark.invoicedAt)} by {mark.invoicedBy}
                </span>
              ) : review ? (
                <span className="badge b-warning">
                  <Icon name="alert" />
                  Review {review} {review === 1 ? 'item' : 'items'}
                </span>
              ) : (
                <span className="badge b-good">
                  <Icon name="check" />
                  Ready to invoice
                </span>
              )}
            </div>
          </div>
        </section>

        <div className="alert">
          <Icon name="info" />
          <div>
            Invoices use the Microsoft Graph count. Differences are listed so they can be explained before invoicing
            {pstnBannerTail(willSync, total, reconciliation, month)}
          </div>
        </div>

        <div className="grid">
          <section className="card c-7">
            <div className="card-head">
              <div>
                <div className="t">By day</div>
                <div className="d">Days with a difference are highlighted; days before the call history are not</div>
              </div>
            </div>
            <div className="card-body">
              <div className="cal">
                {DAY_HEADS.map((d) => (
                  <div key={d} className="dh">
                    {d}
                  </div>
                ))}
                {cells.map((cell) =>
                  cell.blank ? (
                    <div key={cell.key} className="day blank" />
                  ) : (
                    <div
                      key={cell.key}
                      className={['day', cell.diff && !cell.beforeHistory ? 'diff' : cell.weekend || cell.beforeHistory ? 'off' : ''].filter(Boolean).join(' ')}
                      title={`${monthLabel(month).split(' ')[0].slice(0, 3)} ${cell.day}: Graph ${cell.graph}, stored ${cell.stored}${cell.beforeHistory ? ", before the account's call history" : ''}`}
                    >
                      <span className="d">{cell.day}</span>
                      <span className="g">Graph {integer(cell.graph)}</span>
                      <span className="g">Stored {integer(cell.stored)}</span>
                      {cell.beforeHistory && <span className="g">Before history</span>}
                      {!cell.beforeHistory && cell.diff > 0 && (
                        <span className="x">
                          <Icon name="alert" className="ico-sm" /> {cell.diff} missing
                        </span>
                      )}
                      {!cell.beforeHistory && cell.diff < 0 && <span className="x">{-cell.diff} not in Graph</span>}
                    </div>
                  )
                )}
              </div>
            </div>
          </section>
          <section className="card c-5">
            <div className="card-head">
              <div>
                <div className="t">Differences</div>
                <div className="d">
                  Calls in the Graph PSTN log but not stored ({Math.min(SHOWN_DIFFERENCES, differences.length)} of {integer(total)} shown)
                </div>
              </div>
            </div>
            <div className="card-body flush">
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {differences.slice(0, SHOWN_DIFFERENCES).map(({ row, status, tone, reason }) => {
                  const direction = pstnDirection(row.callType);
                  return (
                    <li key={row.id} style={{ padding: '10px 16px', borderTop: '1px solid var(--divider)' }}>
                      <div className="row between">
                        <span className="small strong num">{shortDateTime(row.startDateTime)}</span>
                        <span className={`badge ${tone === 'warning' ? 'b-warning' : 'b-info'}`}>{status}</span>
                      </div>
                      <div className="caption">
                        {direction} · {formatPhone(direction === 'Outbound' ? row.calleeNumber : row.callerNumber)} · {durationText(row.durationSeconds)}
                      </div>
                      <div className="small sub">{plainSyncReason(reason)}</div>
                    </li>
                  );
                })}
              </ul>
              {!differences.length && (
                <p className="caption" style={{ padding: '12px 16px', margin: 0, borderTop: '1px solid var(--divider)' }}>
                  Every Graph PSTN call this month has a stored call record.
                </p>
              )}
            </div>
          </section>
        </div>
      </>
    );
  }

  return (
    <div className="stack-lg">
      {header}
      {body}
      <MarkDialog
        open={markOpen}
        reconciliation={reconciliation}
        month={month}
        onClose={() => setMarkOpen(false)}
        onMarked={(m) => {
          setMarkOpen(false);
          setData((d) => ({ ...d, mark: m }));
        }}
      />
    </div>
  );
}
